import { motion } from 'framer-motion';

/* ---------------- Shared tide types + helpers ---------------- */

export interface TideEvent { time: string; height: number; type: 'high' | 'low'; }
export interface DayTides { date: string; dayName: string; tides: TideEvent[]; }
export interface TidePoint { t: number; h: number; type?: 'high' | 'low'; }
export interface FishingPeriod { start: string; end: string; type: string; }
export interface FishingDay {
  date: string; dayName: string; rating: number; ratingLabel: string;
  majorPeriods: FishingPeriod[]; minorPeriods: FishingPeriod[];
}

export const RATING_STARS = (n: number) => '★'.repeat(n) + '☆'.repeat(5 - n);

export function fmtTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-NZ', { hour: '2-digit', minute: '2-digit', hour12: true });
}
export function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short' });
}

// Cosine interpolation between successive high/low extrema (how real tide curves move)
export function heightAt(events: TidePoint[], t: number): number {
  if (!events.length) return 1.5;
  let after = events.findIndex(e => e.t > t);
  if (after === -1) after = events.length;
  const a = events[after - 1];
  const b = events[after];
  if (!a && b) return b.h;
  if (a && !b) return a.h;
  if (a && b) {
    const span = b.t - a.t;
    if (span <= 0) return b.h;
    const f = (1 - Math.cos(Math.PI * (t - a.t) / span)) / 2;
    return a.h + (b.h - a.h) * f;
  }
  return 1.5;
}

/* ---------------- Rating badge ---------------- */

export function RatingBadge({ rating, label }: { rating: number; label: string }) {
  const colors = ['#ef4444','#f97316','#eab308','#22c55e','#06b6d4','#a855f7'];
  const color = colors[rating] || colors[0];
  return (
    <motion.div
      initial={{ scale: 0, rotate: -180 }}
      animate={{ scale: 1, rotate: 0 }}
      transition={{ type: 'spring', stiffness: 200, damping: 15 }}
      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full font-bold text-xs"
      style={{ background: `${color}20`, border: `1px solid ${color}40`, color }}
    >
      <span className="text-base">{RATING_STARS(rating)}</span>
      {label && <span>{label}</span>}
    </motion.div>
  );
}

/* ---------------- Fishing today card ---------------- */

export function FishingTodayCard({ fishing }: { fishing: FishingDay }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl bg-gradient-to-br from-surface-light to-card-light dark:from-surface dark:to-card border border-[rgba(16,42,66,0.09)] p-4 mb-4"
    >
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-text-muted text-[0.65rem] uppercase tracking-wider">Today's Fishing</div>
          <div className="font-display text-base font-bold">{fishing.ratingLabel}</div>
        </div>
        <RatingBadge rating={fishing.rating} label="" />
      </div>

      <div className="text-[0.65rem] font-bold text-text-secondary mb-1.5 uppercase tracking-wider">🐟 Major Feeding Times</div>
      <div className="grid grid-cols-2 gap-2 mb-3">
        {fishing.majorPeriods.map((p, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1 + i * 0.1 }}
            className="rounded-xl bg-accent/5 border border-accent/20 px-3 py-2"
          >
            <div className="font-bold text-sm">{p.start}</div>
            <div className="text-text-muted text-[0.6rem]">to {p.end}</div>
          </motion.div>
        ))}
      </div>

      <div className="text-[0.65rem] font-bold text-text-secondary mb-1.5 uppercase tracking-wider">Minor Times</div>
      <div className="grid grid-cols-2 gap-2">
        {fishing.minorPeriods.map((p, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3 + i * 0.1 }}
            className="rounded-xl bg-surface-light dark:bg-surface border border-[rgba(79,179,217,0.08)] px-3 py-2"
          >
            <div className="font-semibold text-sm">{p.start}</div>
            <div className="text-text-muted text-[0.6rem]">to {p.end}</div>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
}

/* ---------------- 7-day tide forecast ---------------- */

export function SevenDayTides({ days }: { days: DayTides[] }) {
  return (
    <div className="rounded-2xl border border-[rgba(16,42,66,0.09)] overflow-hidden mb-4">
      <div className="bg-surface-light dark:bg-surface px-4 py-2.5 border-b border-[rgba(16,42,66,0.09)]">
        <span className="font-display text-xs font-bold uppercase tracking-wider">7-Day Tide Forecast</span>
      </div>
      <div className="divide-y divide-[rgba(79,179,217,0.05)]">
        {days.map((day, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: i * 0.05 }}
            className="px-3 py-2.5 flex items-center gap-2"
          >
            <div className="w-14 flex-shrink-0">
              <div className="font-semibold text-xs">{i === 0 ? 'Today' : day.dayName.slice(0, 3)}</div>
              <div className="text-text-muted text-[0.65rem]">{fmtDate(day.date)}</div>
            </div>
            <div className="flex-1 flex items-end gap-1">
              {day.tides.map((t, j) => (
                <div key={j} className="flex-1 text-center">
                  <div className={`text-[0.7rem] font-bold ${t.type === 'high' ? 'text-accent' : 'text-text-muted'}`}>
                    {fmtTime(t.time)}
                  </div>
                  <div className="text-text-muted text-[0.6rem]">{t.height}m</div>
                  <div className="h-8 flex items-end mt-0.5">
                    <motion.div
                      initial={{ height: 0 }}
                      animate={{ height: `${Math.max(15, (t.height / 3) * 100)}%` }}
                      transition={{ delay: i * 0.05 + j * 0.03, type: 'spring', stiffness: 100 }}
                      className={`w-full rounded-sm ${t.type === 'high' ? 'bg-accent/40' : 'bg-accent/15'}`}
                    />
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- 7-day fishing forecast ---------------- */

export function SevenDayFishing({ days }: { days: FishingDay[] }) {
  return (
    <div className="rounded-2xl border border-[rgba(16,42,66,0.09)] overflow-hidden mb-4">
      <div className="bg-surface-light dark:bg-surface px-4 py-2.5 border-b border-[rgba(16,42,66,0.09)]">
        <span className="font-display text-xs font-bold uppercase tracking-wider">7-Day Fishing Forecast</span>
      </div>
      <div className="divide-y divide-[rgba(79,179,217,0.05)]">
        {days.map((day, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: i * 0.06 }}
            className="px-3 py-2.5 flex items-center gap-3"
          >
            <div className="w-12 flex-shrink-0">
              <div className="font-semibold text-xs">{i === 0 ? 'Today' : day.dayName.slice(0, 3)}</div>
              <div className="text-text-muted text-[0.6rem]">{fmtDate(day.date)}</div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[0.7rem] text-text-secondary truncate">
                {day.majorPeriods.map(p => `${p.start}–${p.end}`).join(' · ')}
              </div>
            </div>
            <div className="flex-shrink-0 text-right">
              <div className="text-xs">{RATING_STARS(day.rating)}</div>
              <div className="text-text-muted text-[0.6rem]">{day.ratingLabel}</div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
